import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Users as UsersIcon, Trash2, XCircle, CheckCircle, Search, X } from 'lucide-react';
import CountUp from 'react-countup';
import { usePageHeader } from '../components/Layout';
import { SectionCard, AreaChart, Chip } from '../design/primitives';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const EVENT_LABEL = {
  new_task: 'Yeni Görev',
  comment: 'Yorum',
  file_upload: 'Dosya',
  revision: 'Revizyon',
  review: 'İnceleme',
  workflow_progress: 'İş Akışı',
  task_update: 'Güncelleme',
  task_delete: 'Silme',
  project_created: 'Proje',
};

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'yonetici', label: 'Yönetici' },
  { value: 'proje_yoneticisi', label: 'Proje Yöneticisi' },
  { value: 'takım_lideri', label: 'Takım Lideri' },
  { value: 'takım_üyesi', label: 'Takım Üyesi' },
];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  );
}

export default function NotificationHistory() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canSend = ['admin', 'super_admin', 'yonetici'].includes(user?.role);

  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');

  const [sendOpen, setSendOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  const [subsStats, setSubsStats] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [deleteFailedOpen, setDeleteFailedOpen] = useState(false);
  const [deleteOneOpen, setDeleteOneOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [sendForm, setSendForm] = useState({
    title: '',
    body: '',
    icon: '',
    url: '/',
    targetingType: 'all',
    targetIds: [],
  });
  const [sending, setSending] = useState(false);
  const [teams, setTeams] = useState([]);
  const [modalUsers, setModalUsers] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStats();
    fetchHistory();
  }, [page, filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/push/history/stats`);
      setStats(res.data);
    } catch {
      toast.error('İstatistikler yüklenemedi');
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 15,
        ...(search && { search }),
        ...(filterType !== 'all' && { notification_type: filterType }),
      };
      const res = await axios.get(`${API}/push/history`, { params });
      setHistory(res.data.history);
      setTotalPages(res.data.totalPages);
      setFilteredTotal(res.data.total);
    } catch {
      toast.error('Geçmiş yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const doSearch = () => {
    setPage(1);
    fetchHistory();
  };

  const confirmDeleteFailed = async () => {
    try {
      setLoading(true);
      const res = await axios.delete(`${API}/push/history/failed`);
      if (res.data.success) {
        toast.success(res.data.message);
        setDeleteFailedOpen(false);
        setTimeout(() => {
          fetchHistory();
          fetchStats();
        }, 200);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıtlar silinemedi');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteOne = (item) => {
    setPendingDelete(item);
    setDeleteOneOpen(true);
  };

  const confirmDeleteOne = async () => {
    if (!pendingDelete) return;
    try {
      const res = await axios.delete(`${API}/push/history/${pendingDelete.id}`);
      if (res.data.success) {
        toast.success('Bildirim silindi');
        setDeleteOneOpen(false);
        setPendingDelete(null);
        fetchHistory();
        fetchStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const openSubs = async () => {
    setSubsOpen(true);
    setLoadingSubs(true);
    try {
      const res = await axios.get(`${API}/push/subscriptions/stats`);
      setSubsStats(res.data);
    } catch {
      toast.error('Abonelik istatistikleri yüklenemedi');
    } finally {
      setLoadingSubs(false);
    }
  };

  const openSend = async () => {
    try {
      const [tRes, uRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/users`),
      ]);
      setTeams(tRes.data);
      setModalUsers(uRes.data);
    } catch {
      /* noop */
    }
    setSendForm({
      title: '',
      body: '',
      icon: '',
      url: '/',
      targetingType: 'all',
      targetIds: [],
    });
    setSendOpen(true);
  };

  const toggleTargetId = (id) => {
    setSendForm((f) => ({
      ...f,
      targetIds: f.targetIds.includes(id)
        ? f.targetIds.filter((x) => x !== id)
        : [...f.targetIds, id],
    }));
  };

  const selectAllTargets = (items) => {
    const ids = items.map((x) => x.id || x.value);
    const all = ids.every((id) => sendForm.targetIds.includes(id));
    setSendForm((f) => ({ ...f, targetIds: all ? [] : ids }));
  };

  const doSend = async (e) => {
    e.preventDefault();
    if (!sendForm.title.trim() || !sendForm.body.trim()) {
      toast.error('Başlık ve mesaj zorunludur');
      return;
    }
    setSending(true);
    try {
      const payload = {
        title: sendForm.title,
        body: sendForm.body,
        icon: sendForm.icon || '/onxflow_logo.png',
        url: sendForm.url || '/',
        targetingType: sendForm.targetingType,
        targetIds:
          sendForm.targetingType !== 'all' && sendForm.targetIds.length > 0
            ? sendForm.targetIds
            : null,
      };
      const res = await axios.post(`${API}/push/send`, payload);
      toast.success(
        `Bildirim gönderildi • ${res.data.successful} başarılı` +
          (res.data.failed > 0 ? `, ${res.data.failed} başarısız` : '')
      );
      setSendOpen(false);
      setPage(1);
      setTimeout(() => {
        fetchHistory();
        fetchStats();
      }, 200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bildirim gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const breadcrumb = useMemo(
    () => [{ label: 'Bildirim Geçmişi' }],
    []
  );
  const headerActions = useMemo(() => {
    if (!canSend) return null;
    return (
      <>
        <button className="ox-btn" onClick={openSubs}>
          <UsersIcon size={14} /> Abonelikler
        </button>
        <button className="ox-btn ox-btn-primary" onClick={openSend}>
          <Send size={14} /> Bildirim Gönder
        </button>
      </>
    );
  }, [canSend]);
  usePageHeader(breadcrumb, headerActions, [canSend]);

  const dailyTrend =
    stats?.dailyTrend?.map((d) => ({ d: d._id?.slice(5) || '', v: d.count })) ||
    [];
  const hourly =
    stats?.hourlyDistribution?.map((h) => ({
      d: String(h._id).padStart(2, '0'),
      v: h.count,
    })) || [];

  return (
    <div className="ox-page space-y-5">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            label="Toplam Gönderim"
            value={<CountUp end={stats.totalSent} duration={1.2} separator="." />}
          />
          <StatTile
            label="Bu Ay"
            value={<CountUp end={stats.thisMonth} duration={1.2} separator="." />}
          />
          <StatTile
            label="Bu Hafta"
            value={<CountUp end={stats.thisWeek} duration={1.2} separator="." />}
          />
          <StatTile
            label="Başarı Oranı"
            value={
              <>
                <CountUp
                  end={parseFloat(stats.successRate)}
                  duration={1.2}
                  decimals={1}
                />
                %
              </>
            }
          />
        </div>
      )}

      {/* Charts */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:col-span-2">
            <SectionCard title="Son 30 Gün Trendi">
              {dailyTrend.length > 0 ? (
                <AreaChart data={dailyTrend} height={170} />
              ) : (
                <div className="text-center py-8 text-sm text-[color:var(--text-muted)]">
                  Veri yok
                </div>
              )}
            </SectionCard>
          </div>
          <SectionCard title="Saatlik Dağılım">
            {hourly.length > 0 ? (
              <AreaChart data={hourly} height={160} color="var(--warn)" />
            ) : (
              <div className="text-center py-8 text-sm text-[color:var(--text-muted)]">
                Veri yok
              </div>
            )}
          </SectionCard>
          <SectionCard title="Olay Türleri">
            {stats.byEvent && stats.byEvent.length > 0 ? (
              <div className="space-y-2">
                {stats.byEvent
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 8)
                  .map((item) => {
                    const max = stats.byEvent[0]?.count || 1;
                    const pct = (item.count / max) * 100;
                    return (
                      <div key={item._id}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span>{EVENT_LABEL[item._id] || item._id}</span>
                          <span className="ox-mono">{item.count}</span>
                        </div>
                        <div className="ox-track">
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              background: 'var(--accent)',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-sm text-[color:var(--text-muted)]">
                Henüz otomatik bildirim verisi yok
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* Table */}
      <div className="ox-surface overflow-hidden">
        <div
          className="px-4 py-2.5 flex flex-col md:flex-row gap-2 items-start md:items-center justify-between"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold">Bildirim Geçmişi</span>
            <Chip mono>
              {(search || filterType !== 'all'
                ? filteredTotal
                : stats?.totalSent || 0
              ).toLocaleString('tr-TR')}
            </Chip>
          </div>
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]">
                <Search size={14} />
              </span>
              <input
                type="text"
                placeholder="Ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                className="ox-input pl-8 md:w-48"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
              className="ox-select md:w-36"
            >
              <option value="all">Tümü</option>
              <option value="manual">Manuel</option>
              <option value="automatic">Otomatik</option>
            </select>
            {isAdmin && stats?.totalCompletelyFailed > 0 && (
              <button
                className="ox-btn ox-btn-danger"
                onClick={() => setDeleteFailedOpen(true)}
                title="Tamamen başarısız kayıtları sil"
              >
                <XCircle size={14} />
                Başarısızları Sil ({stats.totalCompletelyFailed})
              </button>
            )}
          </div>
        </div>

        {loading && history.length === 0 ? (
          <div className="p-10 text-center text-sm ox-text-muted">
            Yükleniyor...
          </div>
        ) : history.length === 0 ? (
          <div className="p-10 text-center text-sm ox-text-muted">
            Henüz bildirim geçmişi yok
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="ox-surface-muted">
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                      Tarih
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                      Başlık
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)] hidden md:table-cell">
                      Mesaj
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)] hidden lg:table-cell">
                      Gönderen
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                      Tür
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                      Hedef
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                      Durum
                    </th>
                    {isAdmin && (
                      <th className="px-3 py-2 text-right w-16"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr
                      key={item.id}
                      className="ox-row-hover"
                      style={{ borderBottom: '1px solid var(--border)' }}
                    >
                      <td className="px-3 py-2.5 ox-mono text-xs whitespace-nowrap">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="px-3 py-2.5 font-medium truncate max-w-[180px]">
                        {item.title}
                      </td>
                      <td className="px-3 py-2.5 text-xs ox-text-muted truncate max-w-[220px] hidden md:table-cell">
                        {item.body}
                      </td>
                      <td className="px-3 py-2.5 text-xs ox-text-muted truncate max-w-[140px] hidden lg:table-cell">
                        {item.sentByName}
                      </td>
                      <td className="px-3 py-2.5">
                        <Chip>
                          {item.notificationType === 'manual'
                            ? 'Manuel'
                            : 'Otomatik'}
                        </Chip>
                      </td>
                      <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                        <span className="ox-mono">
                          {item.targetUserIds.length}
                        </span>{' '}
                        kullanıcı
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className="inline-flex items-center gap-1"
                            style={{ color: 'var(--good)' }}
                          >
                            <CheckCircle size={12} />
                            <span className="ox-mono">{item.successCount}</span>
                          </span>
                          {item.failCount > 0 && (
                            <span
                              className="inline-flex items-center gap-1"
                              style={{ color: 'var(--danger)' }}
                            >
                              <XCircle size={12} />
                              <span className="ox-mono">{item.failCount}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2.5 text-right">
                          <button
                            className="ox-btn ox-btn-ghost"
                            style={{ color: 'var(--danger)' }}
                            onClick={() => openDeleteOne(item)}
                            title="Sil"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div
                className="flex items-center justify-between px-3 py-2.5 ox-surface-muted"
                style={{ borderTop: '1px solid var(--border)' }}
              >
                <div className="text-xs ox-text-muted">
                  Sayfa{' '}
                  <span className="ox-mono">
                    {page}/{totalPages}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="ox-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Önceki
                  </button>
                  <button
                    className="ox-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Sonraki
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Send Modal */}
      {sendOpen && (
        <div className="ox-modal-backdrop" onClick={() => setSendOpen(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={doSend}>
              <div className="ox-modal-head">
                <h3>Push Bildirim Gönder</h3>
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setSendOpen(false)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="ox-modal-body space-y-3">
                <div className="ox-field">
                  <label>Başlık * ({sendForm.title.length}/100)</label>
                  <input
                    value={sendForm.title}
                    onChange={(e) =>
                      setSendForm({ ...sendForm, title: e.target.value })
                    }
                    className="ox-input"
                    placeholder="Bildirim başlığı"
                    maxLength={100}
                    required
                  />
                </div>
                <div className="ox-field">
                  <label>Mesaj * ({sendForm.body.length}/500)</label>
                  <textarea
                    value={sendForm.body}
                    onChange={(e) =>
                      setSendForm({ ...sendForm, body: e.target.value })
                    }
                    className="ox-textarea"
                    rows={3}
                    maxLength={500}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="ox-field">
                    <label>Yönlendirme URL'i</label>
                    <input
                      value={sendForm.url}
                      onChange={(e) =>
                        setSendForm({ ...sendForm, url: e.target.value })
                      }
                      className="ox-input"
                      placeholder="/tasks"
                    />
                  </div>
                  <div className="ox-field">
                    <label>Hedef Kitle *</label>
                    <select
                      value={sendForm.targetingType}
                      onChange={(e) =>
                        setSendForm({
                          ...sendForm,
                          targetingType: e.target.value,
                          targetIds: [],
                        })
                      }
                      className="ox-select"
                    >
                      <option value="all">Tüm Kullanıcılar</option>
                      <option value="role">Rol Bazlı</option>
                      <option value="team">Takım Bazlı</option>
                      <option value="selected">Seçili Kullanıcılar</option>
                    </select>
                  </div>
                </div>

                {sendForm.targetingType === 'role' && (
                  <TargetPicker
                    label="Roller"
                    items={ROLES.map((r) => ({ id: r.value, name: r.label }))}
                    selected={sendForm.targetIds}
                    onToggle={toggleTargetId}
                    onSelectAll={() => selectAllTargets(ROLES)}
                  />
                )}
                {sendForm.targetingType === 'team' && (
                  <TargetPicker
                    label="Takımlar"
                    items={teams}
                    selected={sendForm.targetIds}
                    onToggle={toggleTargetId}
                    onSelectAll={() => selectAllTargets(teams)}
                  />
                )}
                {sendForm.targetingType === 'selected' && (
                  <TargetPicker
                    label="Kullanıcılar"
                    items={modalUsers.map((u) => ({
                      id: u.id,
                      name: `${u.name} (${u.email})`,
                    }))}
                    selected={sendForm.targetIds}
                    onToggle={toggleTargetId}
                    onSelectAll={() =>
                      selectAllTargets(
                        modalUsers.map((u) => ({ id: u.id, name: u.name }))
                      )
                    }
                  />
                )}
              </div>
              <div className="ox-modal-foot">
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setSendOpen(false)}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="ox-btn ox-btn-primary"
                  disabled={sending}
                >
                  <Send size={14} />
                  {sending ? 'Gönderiliyor...' : 'Gönder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscriptions Modal */}
      {subsOpen && (
        <div className="ox-modal-backdrop" onClick={() => setSubsOpen(false)}>
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <h3>Aktif Abonelikler</h3>
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setSubsOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body space-y-3">
              {loadingSubs ? (
                <div className="text-center py-6 text-sm ox-text-muted">
                  Yükleniyor...
                </div>
              ) : subsStats ? (
                <>
                  <div className="ox-surface-muted p-4 rounded-md">
                    <div className="text-[11px] ox-text-muted uppercase tracking-wider mb-1">
                      Aktif Abonelik
                    </div>
                    <div className="text-3xl font-semibold ox-mono">
                      {subsStats.activeSubscriptions}
                    </div>
                  </div>
                  {subsStats.byUser?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-2">
                        Kullanıcı Bazlı Dağılım
                      </div>
                      <div
                        className="space-y-1 max-h-60 overflow-y-auto ox-scroll"
                      >
                        {subsStats.byUser.map((item) => (
                          <div
                            key={item.userId}
                            className="flex items-center justify-between p-2 ox-surface-muted rounded"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">
                                {item.userName}
                              </div>
                              <div className="text-[11px] ox-text-muted truncate">
                                {item.userEmail}
                              </div>
                            </div>
                            <Chip mono>{item.count} cihaz</Chip>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-6 text-sm ox-text-muted">
                  Abonelik verisi bulunamadı
                </div>
              )}
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setSubsOpen(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete failed modal */}
      {deleteFailedOpen && (
        <div
          className="ox-modal-backdrop"
          onClick={() => setDeleteFailedOpen(false)}
        >
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <h3>Başarısız Kayıtları Sil</h3>
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteFailedOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <p className="text-sm">
                Tamamen başarısız bildirim kayıtlarını silmek istediğinizden
                emin misiniz? Kısmen başarılı olan kayıtlar silinmez.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteFailedOpen(false)}
              >
                İptal
              </button>
              <button className="ox-btn ox-btn-danger" onClick={confirmDeleteFailed}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete one modal */}
      {deleteOneOpen && pendingDelete && (
        <div
          className="ox-modal-backdrop"
          onClick={() => setDeleteOneOpen(false)}
        >
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <h3>Bildirimi Sil</h3>
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOneOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <p className="text-sm">
                <strong>{pendingDelete.title}</strong> bildirimini silmek
                istediğinizden emin misiniz?
              </p>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOneOpen(false)}
              >
                İptal
              </button>
              <button className="ox-btn ox-btn-danger" onClick={confirmDeleteOne}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TargetPicker({ label, items, selected, onToggle, onSelectAll }) {
  const allSelected = items.length > 0 && items.every((i) => selected.includes(i.id));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11.5px] font-medium text-[color:var(--text-muted)]">
          {label} ({selected.length} seçili)
        </label>
        <button
          type="button"
          onClick={onSelectAll}
          className="text-[11px] ox-accent-text hover:underline"
        >
          {allSelected ? 'Hiçbirini seçme' : 'Tümünü seç'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto ox-scroll p-1">
        {items.length === 0 && (
          <span className="text-xs ox-text-muted">Öğe yok</span>
        )}
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            className="ox-pick"
            data-on={selected.includes(it.id)}
            onClick={() => onToggle(it.id)}
          >
            {it.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="ox-surface p-4">
      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-semibold ox-mono mt-1">{value}</div>
    </div>
  );
}
